import type { IncidentType } from '@/gen/types/IncidentType';
import { incidentTypeEnum } from '@/gen/types/IncidentType';
import { defaultIncidentAudience, incidentAudienceEnum, type IncidentAudience } from './audience';

export type IncidentTypeRegistryEntry = {
    createEndpoint: string;
    customerCreateEndpoint?: string;
    routeAliases: string[];
};

function normalizeIncidentTypeLookup(value: string) {
    return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

const incidentTypeRegistry: Record<IncidentType, IncidentTypeRegistryEntry> = {
    [incidentTypeEnum.ActionableObservation]: {
        createEndpoint: 'incidents/actionable-observation',
        customerCreateEndpoint: 'incidents/customer/actionable-observation',
        routeAliases: ['actionable-observation', 'actionable-observations'],
    },
    [incidentTypeEnum.PositiveObservation]: {
        createEndpoint: 'incidents/positive-observation',
        customerCreateEndpoint: 'incidents/customer/positive-observation',
        routeAliases: ['positive-observation', 'positive-observations'],
    },
    [incidentTypeEnum.WasteManagement]: {
        createEndpoint: 'incidents/waste-management',
        customerCreateEndpoint: 'incidents/customer/waste-management',
        routeAliases: ['waste-management'],
    },
    [incidentTypeEnum.FireExplosion]: {
        createEndpoint: 'incidents/fire-explosion',
        customerCreateEndpoint: 'incidents/customer/fire-explosion',
        routeAliases: ['fire-explosion'],
    },
    [incidentTypeEnum.ChemicalSpillEmissions]: {
        createEndpoint: 'incidents/chemical-spill-emissions',
        customerCreateEndpoint: 'incidents/customer/chemical-spill-emissions',
        routeAliases: ['chemical-spill-emissions'],
    },
    [incidentTypeEnum.PeopleIncident]: {
        createEndpoint: 'incidents/people-incident',
        customerCreateEndpoint: 'incidents/customer/people-incident',
        routeAliases: ['people-incident'],
    },
    [incidentTypeEnum.NetworkInfrastructure]: {
        createEndpoint: 'incidents/network-infrastructure',
        customerCreateEndpoint: 'incidents/customer/network-infrastructure',
        routeAliases: ['network-infrastructure'],
    },
    [incidentTypeEnum.FacilityInfrastructure]: {
        createEndpoint: 'incidents/facility-infrastructure',
        customerCreateEndpoint: 'incidents/customer/facility-infrastructure',
        routeAliases: ['facility-infrastructure'],
    },
    [incidentTypeEnum.CapacityPerformance]: {
        createEndpoint: 'incidents/capacity-performance',
        routeAliases: ['capacity-performance'],
    },
    [incidentTypeEnum.ChangeOrConfigurationError]: {
        createEndpoint: 'incidents/change-or-configuration-error',
        routeAliases: ['change-or-configuration-error', 'change-configuration-error'],
    },
    [incidentTypeEnum.PhysicalInfrastructure]: {
        createEndpoint: 'incidents/physical-infrastructure',
        customerCreateEndpoint: 'incidents/customer/physical-infrastructure',
        routeAliases: ['physical-infrastructure'],
    },
    [incidentTypeEnum.CoolingHVAC]: {
        createEndpoint: 'incidents/cooling-hvac',
        customerCreateEndpoint: 'incidents/customer/cooling-hvac',
        routeAliases: ['cooling-hvac'],
    },
    [incidentTypeEnum.PowerElectricalSystems]: {
        createEndpoint: 'incidents/power-electrical-systems',
        customerCreateEndpoint: 'incidents/customer/power-electrical-systems',
        routeAliases: ['power-electrical-systems'],
    },
    [incidentTypeEnum.CustomerSatisfaction]: {
        createEndpoint: 'incidents/customer-satisfaction',
        customerCreateEndpoint: 'incidents/customer/customer-satisfaction',
        routeAliases: ['customer-satisfaction'],
    },
    [incidentTypeEnum.LegalRegulatory]: {
        createEndpoint: 'incidents/legal-regulatory',
        routeAliases: ['legal-regulatory'],
    },
    [incidentTypeEnum.PolicyCompliance]: {
        createEndpoint: 'incidents/policy-compliance',
        routeAliases: ['policy-compliance'],
    },
    [incidentTypeEnum.SupplierPerformance]: {
        createEndpoint: 'incidents/supplier-performance',
        routeAliases: ['supplier-performance'],
    },
    [incidentTypeEnum.ServiceQualityContractualDeviation]: {
        createEndpoint: 'incidents/service-quality-contractual-deviation',
        customerCreateEndpoint: 'incidents/customer/service-quality-contractual-deviation',
        routeAliases: ['service-quality-contractual-deviation', 'service-quality-contractual-obligation'],
    },
    [incidentTypeEnum.ITSystemsNetwork]: {
        createEndpoint: 'incidents/it-systems-network',
        routeAliases: ['it-systems-network'],
    },
    [incidentTypeEnum.ThirdParty]: {
        createEndpoint: 'incidents/third-party',
        routeAliases: ['third-party'],
    },
    [incidentTypeEnum.InformationSecurityCybersecurity]: {
        createEndpoint: 'incidents/information-security-cybersecurity',
        routeAliases: ['information-security-cybersecurity', 'information-cyber-security'],
    },
    [incidentTypeEnum.PhysicalSecurity]: {
        createEndpoint: 'incidents/physical-security',
        customerCreateEndpoint: 'incidents/customer/physical-security',
        routeAliases: ['physical-security'],
    },
};

const incidentTypeLookup = new Map<string, IncidentType>();

for (const [incidentType, entry] of Object.entries(incidentTypeRegistry) as Array<
    [IncidentType, IncidentTypeRegistryEntry]
>) {
    incidentTypeLookup.set(normalizeIncidentTypeLookup(incidentType), incidentType);
    for (const alias of entry.routeAliases) {
        incidentTypeLookup.set(normalizeIncidentTypeLookup(alias), incidentType);
    }
}

export function resolveIncidentType(value: string): IncidentType | null {
    if (!value) return null;
    return incidentTypeLookup.get(normalizeIncidentTypeLookup(value)) ?? null;
}

export function getIncidentTypeRegistryEntry(incidentType: IncidentType): IncidentTypeRegistryEntry {
    return incidentTypeRegistry[incidentType];
}

export function isIncidentAudienceSupported(
    incidentType: IncidentType,
    audience: IncidentAudience = defaultIncidentAudience,
): boolean {
    const entry = incidentTypeRegistry[incidentType];
    if (audience === incidentAudienceEnum.Customer) {
        return Boolean(entry.customerCreateEndpoint);
    }

    return true;
}

export function getIncidentCreateEndpoint(
    incidentType: IncidentType,
    audience: IncidentAudience = defaultIncidentAudience,
): string {
    const entry = incidentTypeRegistry[incidentType];

    if (audience === incidentAudienceEnum.Customer) {
        if (!entry.customerCreateEndpoint) {
            throw new Error(`Incident type ${incidentType} does not support customer create.`);
        }

        return entry.customerCreateEndpoint;
    }

    return entry.createEndpoint;
}
